import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isActiveBatchStatus,
  StockPhotoImportBatch,
} from 'src/redux/api/inventory/stockPhotoImports';
import BatchOperationToasts, {
  BatchCompletedStatus,
  BatchOperationItem,
} from 'src/screens/shared/components/BatchOperationToasts';

import { getAccountDisplayInfo } from './helpers';
import { useStockPhotoImport } from './StockPhotoImportContext';

const getCompletedStatus = (
  batch: StockPhotoImportBatch
): { status: BatchCompletedStatus; failedCount?: number } => {
  if (batch.status === 'completed_with_errors') {
    return { status: 'completed_with_errors', failedCount: batch.counts.failed };
  }

  if (batch.status === 'failed') {
    return { status: 'failed' };
  }

  if (batch.status === 'canceled') {
    return { status: 'canceled' };
  }

  return { status: 'completed' };
};

const StockPhotoImportManager = () => {
  const { t } = useTranslation();
  const { batches, cancelBatch, dismissBatch } = useStockPhotoImport();

  const batchItems: BatchOperationItem[] = useMemo(
    () =>
      batches.map((batch) => {
        const { title, thumbnail } = getAccountDisplayInfo(batch);
        const isActive = isActiveBatchStatus(batch.status);

        return {
          id: batch.id,
          title,
          thumbnail,
          progress: isActive ? { current: batch.processed, total: batch.total } : undefined,
          completedStatus: !isActive ? getCompletedStatus(batch) : undefined,
          isProcessing: isActive,
          buttons: {
            cancel: isActive
              ? {
                  show: true,
                  onClick: () => cancelBatch(batch),
                }
              : undefined,
            dismiss: !isActive
              ? {
                  show: true,
                  onClick: () => dismissBatch(batch),
                }
              : undefined,
          },
        };
      }),
    [batches, cancelBatch, dismissBatch]
  );

  const summaryText = t('accountsScreen:stock_import_multiple', { count: batches.length });

  return <BatchOperationToasts batches={batchItems} summaryText={summaryText} />;
};

export default StockPhotoImportManager;
